import React from "react";
import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { Item } from "../../../state/selectors";

const TELIA_PURPLE = "#990ae3";
const BORDER_COLOR = "#e0e0e0";
const GRAY_BG = "#f5f5f5";
const GREEN_BG = "#e6f9ed";
const GREEN_TEXT = "#1a7a3a";

const s = StyleSheet.create({
    section: {
        marginBottom: 12,
    },
    sectionHeading: {
        fontSize: 9,
        fontFamily: "Helvetica-Bold",
        color: "#555555",
        marginBottom: 6,
        textTransform: "uppercase",
    },
    headerRow: {
        flexDirection: "row",
        paddingVertical: 4,
        borderBottomWidth: 1,
        borderBottomColor: BORDER_COLOR,
        borderBottomStyle: "solid",
        backgroundColor: "#fafafa",
    },
    headerCell: {
        fontSize: 8,
        fontFamily: "Helvetica-Bold",
        color: TELIA_PURPLE,
        textAlign: "right",
        width: 80,
    },
    headerCellKey: {
        flex: 1,
        fontSize: 8,
        fontFamily: "Helvetica-Bold",
        color: "#555555",
    },
    row: {
        flexDirection: "row",
        paddingVertical: 4,
        borderBottomWidth: 1,
        borderBottomColor: BORDER_COLOR,
        borderBottomStyle: "solid",
    },
    rowKey: {
        fontSize: 10,
        fontFamily: "Helvetica",
        flex: 1,
    },
    rowCell: {
        fontSize: 10,
        fontFamily: "Helvetica",
        textAlign: "right",
        width: 80,
    },
    rowCellFaded: {
        fontSize: 10,
        fontFamily: "Helvetica",
        color: "#888888",
        textAlign: "right",
        width: 80,
    },
    flashText: {
        fontSize: 9,
        fontFamily: "Helvetica-Oblique",
        color: TELIA_PURPLE,
        marginBottom: 4,
    },
    priceRow: {
        flexDirection: "row",
        gap: 8,
        marginTop: 8,
    },
    priceCard: {
        flex: 1,
        backgroundColor: GRAY_BG,
        borderRadius: 4,
        padding: 10,
    },
    priceCardHighlight: {
        flex: 1,
        backgroundColor: GREEN_BG,
        borderRadius: 4,
        padding: 10,
    },
    pricePeriodLabel: {
        fontSize: 9,
        fontFamily: "Helvetica-Oblique",
        color: TELIA_PURPLE,
        marginBottom: 6,
    },
    savingsBadge: {
        backgroundColor: "#c8f0d8",
        borderRadius: 3,
        padding: 4,
        marginBottom: 6,
    },
    savingsText: {
        fontSize: 8,
        fontFamily: "Helvetica",
        color: GREEN_TEXT,
    },
    priceLineRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginTop: 4,
    },
    priceKeyGroup: {
        flex: 1,
    },
    priceKey: {
        fontSize: 10,
        fontFamily: "Helvetica",
    },
    priceSubKey: {
        fontSize: 8,
        fontFamily: "Helvetica",
        color: "#555555",
        marginTop: 2,
    },
    priceValue: {
        fontSize: 12,
        fontFamily: "Helvetica-Bold",
    },
});

const UpfrontItemsPdf: React.FC<{
    flashText?: string;
    upfrontItems: Item[];
    heading?: string;
}> = ({ flashText, upfrontItems, heading }) => (
    <View style={s.section}>
        {flashText && <Text style={s.flashText}>{flashText}</Text>}
        {heading && <Text style={s.sectionHeading}>{heading}</Text>}
        <View style={s.headerRow}>
            <Text style={s.headerCellKey}></Text>
            <Text style={s.headerCell}>Pris</Text>
        </View>
        {upfrontItems.map((item, index) => (
            <View key={`${item.key}-${index}`} style={s.row}>
                <Text style={s.rowKey}>{item.key}</Text>
                <Text style={s.rowCell}>{item.value}</Text>
            </View>
        ))}
    </View>
);

const MonthlyItemsPdf: React.FC<{
    flashText?: string;
    monthlyItems: Item[];
    heading?: string;
    hasDualPeriod: boolean;
}> = ({ flashText, monthlyItems, heading, hasDualPeriod }) => (
    <View style={s.section}>
        {flashText && <Text style={s.flashText}>{flashText}</Text>}
        {heading && <Text style={s.sectionHeading}>{heading}</Text>}
        <View style={s.headerRow}>
            <Text style={s.headerCellKey}></Text>
            <Text style={s.headerCell}>Første 12 md.</Text>
            {hasDualPeriod && <Text style={s.headerCell}>Etter 12 md.</Text>}
        </View>
        {monthlyItems.map((item, index) => (
            <View key={`${item.key}-${index}`} style={s.row}>
                <Text style={s.rowKey}>{item.key}</Text>
                <Text style={s.rowCell}>{item.value}</Text>
                {hasDualPeriod && (
                    <Text style={item.strikethrough ? s.rowCell : s.rowCellFaded}>
                        {item.strikethrough ?? item.value}
                    </Text>
                )}
            </View>
        ))}
    </View>
);

const TotalPricePdf: React.FC<{
    totalPrice?: number;
    totalDiscountedPrice?: number;
    totalUpfrontPrice?: number;
    totalUpfrontDiscountedPrice?: number;
}> = ({ totalPrice, totalDiscountedPrice, totalUpfrontPrice, totalUpfrontDiscountedPrice }) => {
    const fullPriceMonthly = `${totalPrice ?? 0},-/md.`;
    const discountedPriceMonthly = `${totalDiscountedPrice ?? totalPrice ?? 0},-/md.`;
    const fullPriceYearly = `${(totalPrice ?? 0) * 12},-`;
    const discountedPriceYearly = `${(totalDiscountedPrice ?? totalPrice ?? 0) * 12},-`;
    const upfrontDisplay = `${totalUpfrontDiscountedPrice ?? totalUpfrontPrice ?? 0},-`;
    const savingsFirstYear =
        totalPrice !== undefined && totalDiscountedPrice !== undefined
            ? totalPrice * 12 - totalDiscountedPrice * 12
            : 0;
    const hasDualPeriod = totalDiscountedPrice !== undefined && totalDiscountedPrice !== totalPrice;

    return (
        <View style={s.priceRow}>
            <View style={s.priceCardHighlight}>
                <Text style={s.pricePeriodLabel}>Første 12 md.</Text>
                {savingsFirstYear > 0 && (
                    <View style={s.savingsBadge}>
                        <Text style={s.savingsText}>Du sparer {savingsFirstYear},- første året!</Text>
                    </View>
                )}
                <View style={s.priceLineRow}>
                    <View style={s.priceKeyGroup}>
                        <Text style={s.priceKey}>Per måned</Text>
                        <Text style={s.priceSubKey}>Totalt 12 md. {discountedPriceYearly}</Text>
                    </View>
                    <Text style={s.priceValue}>{discountedPriceMonthly}</Text>
                </View>
                {totalUpfrontDiscountedPrice !== undefined && totalUpfrontDiscountedPrice > 0 && (
                    <View style={[s.priceLineRow, { marginTop: 6 }]}>
                        <Text style={s.priceKey}>Engangskostnad</Text>
                        <Text style={s.priceValue}>{upfrontDisplay}</Text>
                    </View>
                )}
            </View>
            {hasDualPeriod && (
                <View style={s.priceCard}>
                    <Text style={s.pricePeriodLabel}>Etter 12 md.</Text>
                    <View style={s.priceLineRow}>
                        <View style={s.priceKeyGroup}>
                            <Text style={s.priceKey}>Per måned</Text>
                            <Text style={s.priceSubKey}>Totalt 12 md. {fullPriceYearly}</Text>
                        </View>
                        <Text style={s.priceValue}>{fullPriceMonthly}</Text>
                    </View>
                </View>
            )}
        </View>
    );
};

export interface SummaryTablePdfProps {
    flashText?: string;
    items: Item[];
    upfrontItems: Item[];
    totalUpfrontPrice?: number;
    totalUpfrontDiscountedPrice?: number;
    totalDiscountedPrice?: number;
    totalPrice?: number;
}

const SummaryTablePdf: React.FC<SummaryTablePdfProps> = ({
    items,
    upfrontItems,
    flashText,
    totalPrice,
    totalDiscountedPrice,
    totalUpfrontPrice,
    totalUpfrontDiscountedPrice,
}) => {
    const hasDualPeriod = items.some((item) => !!item.strikethrough);
    return (
        <View style={{ marginHorizontal: 16, marginTop: 16 }}>
            {upfrontItems.length > 0 && (
                <UpfrontItemsPdf flashText={flashText} upfrontItems={upfrontItems} heading="INSTALLASJON" />
            )}
            {items.length > 0 && (
                <MonthlyItemsPdf
                    flashText={flashText}
                    monthlyItems={items}
                    heading="DITT ABONNEMENT"
                    hasDualPeriod={hasDualPeriod}
                />
            )}
            <TotalPricePdf
                totalPrice={totalPrice}
                totalDiscountedPrice={totalDiscountedPrice}
                totalUpfrontPrice={totalUpfrontPrice}
                totalUpfrontDiscountedPrice={totalUpfrontDiscountedPrice}
            />
        </View>
    );
};

export default SummaryTablePdf;
